import { useEffect, useId, useRef, useState, type ReactNode } from 'react'
import { Card, CardHeader } from './ui'
import { GridChromeSlot } from './gridLayout'

/**
 * A card whose grid can be thrown full-screen.
 *
 * WHY THIS EXISTS. A report grid lives under a page header, a tab strip, a
 * filter bar and a card header, and on a 15.6in laptop that furniture leaves
 * room for about eight rows. Reviewing a few hundred rows through an
 * eight-row window is the complaint this answers: expand, read, come back.
 *
 * WHY IT IS A WRAPPER RATHER THAN A SECOND <DataGrid> IN AN OVERLAY.
 * The obvious implementation renders the grid twice - once in the card, once
 * in a portal - and it is wrong twice over: the reader loses their scroll
 * position and their sort on every expand, and the two copies can disagree
 * the moment one of them refetches. Here the SAME subtree stays mounted and
 * only its container's class changes, so scroll position, sort, the open row
 * and any in-flight request all survive the transition in both directions.
 *
 * That is also why there is no `display: contents` trick in the collapsed
 * case: the wrapper is a flex column either way, which is exactly what a
 * filling card already expects to be.
 */
export function ExpandableCard({
  title,
  subtitle,
  right,
  titleRepeatsTab = false,
  breadcrumb,
  children,
}: {
  title: string
  subtitle?: string
  right?: ReactNode
  /**
   * Passed through. It hides the TITLE on a short screen and keeps the right
   * slot, which is the behaviour wanted here: the words repeat the tab, the
   * full-screen button does not.
   *
   * rightHidesOnShort is deliberately NOT offered. It would take the toggle
   * away exactly where a reader most wants to expand a grid.
   */
  titleRepeatsTab?: boolean
  /**
   * The trail shown only in full screen, where the page's own header is
   * covered - "Sales" then "Explorer". Full screen hides the tab strip that
   * would otherwise say where you are, so this puts it back.
   */
  breadcrumb?: string[]
  children: ReactNode
}) {
  const [expanded, setExpanded] = useState(false)
  const [slot, setSlot] = useState<HTMLElement | null>(null)
  const panel = useRef<HTMLDivElement | null>(null)
  const opener = useRef<Element | null>(null)
  const labelId = useId()

  // Escape closes, and the focus that opened this gets it back. Both copied
  // from SlideOver rather than reinvented: capture phase with stopPropagation
  // so an Escape meant for this panel never also closes something behind it.
  useEffect(() => {
    if (!expanded) return
    opener.current = document.activeElement

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        setExpanded(false)
        return
      }
      // A crude wraparound trap, the same one SlideOver uses. Tab must not
      // walk out of a full-screen panel into a page the reader cannot see.
      if (e.key !== 'Tab' || !panel.current) return
      const focusable = panel.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
      )
      if (focusable.length === 0) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }
    window.addEventListener('keydown', onKey, true)
    return () => {
      window.removeEventListener('keydown', onKey, true)
      ;(opener.current as HTMLElement | null)?.focus?.()
    }
  }, [expanded])

  const toggle = (
    <button
      type="button"
      onClick={() => setExpanded((v) => !v)}
      aria-expanded={expanded}
      aria-controls={labelId}
      className="no-print inline-flex items-center gap-1.5 rounded-md border border-(--color-line-strong) px-2.5 py-1.5 text-(length:--text-small) font-semibold text-(--color-ink-2) transition-colors hover:bg-(--color-surface-2) hover:text-(--color-ink)"
    >
      {expanded ? (
        <>
          {/* Arrows pointing IN: the icon shows what the click does, not the
              state it is in. */}
          <svg width="13" height="13" viewBox="0 0 16 16" aria-hidden fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10 2v4h4M6 14v-4H2M10 6l5-5M6 10l-5 5" />
          </svg>
          Exit full screen
        </>
      ) : (
        <>
          <svg width="13" height="13" viewBox="0 0 16 16" aria-hidden fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 1H1v5M10 15h5v-5M1 1l5 5M15 15l-5-5" />
          </svg>
          <span className="sr-only">Expand </span>Full screen
        </>
      )}
    </button>
  )

  /* The grid's own controls land here, beside Full screen, rather than on a
     strip of their own above the table — one button is not worth a band of
     height on every report. `display: contents` so the span itself lays out
     nothing; whatever portals in becomes a child of this flex row. */
  const headerRight = (
    <div className="flex items-center gap-2">
      {right}
      {toggle}
      <span ref={setSlot} className="contents" />
    </div>
  )

  return (
    <div
      id={labelId}
      ref={panel}
      role={expanded ? 'dialog' : undefined}
      aria-modal={expanded ? true : undefined}
      aria-label={expanded ? title : undefined}
      className={
        expanded
          ? // z-40 keeps this UNDER the z-50 BrandLoader, so a refresh that
            // covers the screen still covers this. print:static because a
            // reader who expands a grid and prints it should get the grid,
            // not a fixed element painted over one page.
            'anim-panel-in fixed inset-0 z-40 flex flex-col gap-3 bg-(--color-surface-2) p-4 print:static print:p-0'
          : 'flex min-h-0 flex-1 flex-col'
      }
    >
      {expanded ? (
        <div className="no-print flex shrink-0 flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            {breadcrumb && breadcrumb.length > 0 ? (
              <p className="eyebrow">{breadcrumb.join(' / ')}</p>
            ) : null}
            <h2 className="mt-0.5 text-(length:--text-lg) font-semibold text-(--color-ink)">{title}</h2>
          </div>
          {headerRight}
        </div>
      ) : null}

      <Card className="flex min-h-0 flex-1 flex-col">
        {/* In full screen the title is already above the card, so repeating it
            here would cost a band of the height the reader just asked for. */}
        {expanded ? null : (
          <CardHeader
            title={title}
            subtitle={subtitle}
            right={headerRight}
            titleRepeatsTab={titleRepeatsTab}
          />
        )}
        <GridChromeSlot.Provider value={slot}>{children}</GridChromeSlot.Provider>
      </Card>
    </div>
  )
}
